import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

import { SelfServeFormValues } from 'modules/self-serve-sign-up/SelfServeSignupFlow/forms/validation';
import { InboundMerchant } from 'modules/self-serve-sign-up/SelfServeSignupFlow/types';

import {
  SigneeInformation,
  User,
  UserContract,
  DraftVenue,
  UpdateDraftVenueRequest,
  OnboardingStatusData,
} from './types';

const agent = axios.create({
  baseURL: process.env.MERCHANT_ONBOARDING_SERVICE_URI,
});

export const createUser = (user: User, emailToken: String, onboardingToken: String): Promise<{ id: string }> =>
  axios.post(
    `${process.env.MERCHANT_ONBOARDING_SERVICE_URI}/merchant-admin/merchant-sign-up`,
    {
      country: user.country,
      email: user.email,
      first_name: user.firstName,
      last_name: user.lastName,
      phone_number: user.phoneNumber,
      email_token: emailToken,
    },
    {
      headers: {
        Authorization: `Bearer ${onboardingToken}`,
      },
    },
  );

export const createUserForExistingMerchant = (
  user: User,
  emailToken: String,
  onboardingToken: String,
  headers?: AxiosRequestConfig['headers'],
): Promise<{ id: string }> =>
  axios.post(
    `${process.env.MERCHANT_ONBOARDING_SERVICE_URI}/merchant-admin/existing-merchant-sign-up`,
    {
      email: user.email,
      first_name: user.firstName,
      last_name: user.lastName,
      phone_number: user.phoneNumber,
      email_token: emailToken,
    },
    {
      headers: {
        ...headers,
        Authorization: `Bearer ${onboardingToken}`,
      },
    },
  );

export const getUserContractDetails = (): Promise<UserContract[]> =>
  agent.get('/me/contracts').then(response => response.data.results);

export const getOnboardingStatus = (): Promise<OnboardingStatusData> =>
  agent.get('/merchant-admin/me/onboarding-status').then(response => response.data);

export const getSigneeInformation = (token: String): Promise<SigneeInformation> =>
  agent.get(`/merchant-admin/merchant-sign-up?token=${token}`).then(response => response.data);

export const requestSignUpEmailForDraftMerchant = ({
  onboardingToken,
}: {
  onboardingToken: string;
}): Promise<unknown> =>
  agent.post('/merchant-admin/merchant-sign-up/request-sign-up', undefined, {
    headers: {
      Authorization: `Bearer ${onboardingToken}`,
    },
  });

export const requestSignUpEmailForExistingMerchant = ({
  onboardingToken,
}: {
  onboardingToken: string;
}): Promise<unknown> =>
  agent.post('/merchant-admin/existing-merchant-sign-up/request-sign-up', undefined, {
    headers: {
      Authorization: `Bearer ${onboardingToken}`,
    },
  });

export const fetchDraftVenues = (merchantId: string) =>
  agent
    .get<{ results: DraftVenue[] }>(`/merchant-admin/merchants/${merchantId}/draft-venues`)
    .then(response => response.data.results);

export const fetchDraftVenue = (merchantId: string, draftVenueId: string) =>
  agent
    .get<DraftVenue>(`/merchant-admin/merchants/${merchantId}/draft-venues/${draftVenueId}`)
    .then(response => response.data);

export const updateDraftVenue = (
  merchantId: string,
  draftVenueId: string,
  payload: UpdateDraftVenueRequest,
): Promise<unknown> => agent.put(`/merchant-admin/merchants/${merchantId}/draft-venues/${draftVenueId}`, payload);

export const submitDraftVenues = (merchantId: string): Promise<unknown> =>
  agent.post(`/merchant-admin/merchants/${merchantId}/submit-draft-venues`);

export const getCountriesForFeature = (feature: string) =>
  agent
    .get<{ results: Country[] }>(`/merchant-admin/config/features/${feature}`)
    .then(response => response.data.results);

export const signUpOrLinkSelfServeMerchant = ({
  emailToken,
  onboardingToken,
}: {
  emailToken: string;
  onboardingToken: string;
}): Promise<unknown> =>
  agent.post(
    '/merchant-admin/merchant-sign-up/link-inbound',
    { emailToken },
    {
      headers: {
        Authorization: `Bearer ${onboardingToken}`,
      },
    },
  );

export const getInboundMerchant = (merchantId: string): Promise<InboundMerchant> =>
  agent.get(`/merchant-admin/inbound-merchants/${merchantId}`).then(response => response.data);

export const patchInboundMerchant = <T extends Partial<SelfServeFormValues>>(
  payload: T,
  merchantId: string,
): Promise<AxiosResponse['data']> =>
  agent.patch(`/merchant-admin/inbound-merchants/${merchantId}`, payload).then(response => response.data);

export default agent;
